import { useState, useEffect, useContext, CSSProperties } from "react";
import "./AdminActionsCircle.scss";
import OwButton from "@components/shared/OwButton/OwButton";
import UiContext from "@contexts/UiContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import VendorContext from "@contexts/VendorContext";
import { ClickToCopy } from "@components/shared/ClickToCopy/ClickToCopy";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import INotification from "@models/INotification";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import IAdminLoginResultDTO from "@models/DTO/AdminDTO/IAdminLoginResultDTO";
import AdminService from "@services/AdminService";
import { AdminLoginModal } from "@components/admin/AdminLoginModal/AdminLoginModal";

export const AdminActionsCircle = () => {
    const uiContext = useContext(UiContext);

    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const vendorModel = useContext(VendorContext);

    const [isVisible, setIsVisible] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [viewport, setViewport] = useState({ width: 0, height: 0 });
    const [screen, setScreen] = useState({ width: 0, height: 0 });
    const [adminLogin, setAdminLogin] = useState<IAdminLoginResultDTO | null>(null);
    const [showAdminLoginModal, setShowAdminLoginModal] = useState(false);
    const [idleTimeDisplay, setIdleTimeDisplay] = useState(1800);

    const [actionsStyle, setActionsStyle] = useState<CSSProperties>({
        right: "0",
        left: "",
        display: isOpen ? "block" : "none",
    });

    const [style, setStyle] = useState<CSSProperties>({
        right: "2rem",
        left: "",
    });

    useEffect(() => {
        // Set initial values
        updateDimensions();

        // Add event listener
        window.addEventListener("resize", updateDimensions);

        // Load admin login from localStorage
        const savedAdminLogin = AdminService.getAdminLogin();
        if (savedAdminLogin) {
            setAdminLogin(savedAdminLogin);
        }

        // Cleanup
        return () => window.removeEventListener("resize", updateDimensions);
    }, []);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (window.showAdmin) {
                setIsVisible(true);
            }
        });

        // Observe changes to the showHiddenFeatures property on the window object
        const observeShowAdminFeature = () => {
            Object.defineProperty(window, "showAdmin", {
                set: (value) => {
                    if (value === true) {
                        setIsVisible(true);
                    }
                },
                configurable: true, // Allow redefinition
            });
        };

        // Initial observation
        observeShowAdminFeature();

        // Clean up the observer
        return () => {
            observer.disconnect();
            // Reset the property instead of deleting
            Object.defineProperty(window, "showAdmin", {
                value: false,
                writable: true, // Allow modifications later if needed
                configurable: true,
                enumerable: true,
            });
        };
    }, [setIsVisible]);

    const updateDimensions = () => {
        setViewport({
            width: window.innerWidth,
            height: window.innerHeight,
        });
        setScreen({
            width: window.screen.width,
            height: window.screen.height,
        });
    };

    const switchSides = () => {
        if (style.left === "") {
            setStyle({
                left: "2rem",
                right: "",
            });

            setActionsStyle({
                right: "",
                left: "0",
                display: isOpen ? "block" : "none",
            });
        } else {
            setStyle({ left: "", right: "2rem" });

            setActionsStyle({
                right: "0",
                left: "",
                display: isOpen ? "block" : "none",
            });
        }
    };

    const closeTool = () => {
        setIsVisible(false);
    };

    const showToast = () => {
        const notification: INotification = {
            title: "toast_title",
            message: "toast_message",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);
    };

    const handleAdminLoginSuccess = (result: IAdminLoginResultDTO) => {
        AdminService.saveAdminLogin(result);
        setAdminLogin(result);
        setShowAdminLoginModal(false);
    };

    const handleAdminLogout = () => {
        AdminService.clearAdminLogin();
        setAdminLogin(null);

        const notification: INotification = {
            title: "admin_logged_out",
            message: "admin_session_cleared",
            level: NotificationLevelEnum.info,
            position: NotificationPositionEnum.bc,
        };
        uiContext.notifyFn(null, notification);
    };

    useEffect(() => {
        setActionsStyle({
            ...actionsStyle,
            display: isOpen ? "block" : "none",
        });
    }, [isOpen]);

    // Poll the idle timer value every 500ms when the admin circle is open
    useEffect(() => {
        if (!isOpen || uiContext.disableIdleTimeout) {
            return;
        }

        const interval = setInterval(() => {
            const remaining = uiContext.getIdleTimeRemaining();
            setIdleTimeDisplay(remaining);
        }, 500);

        return () => {
            clearInterval(interval);
        };
    }, [isOpen, uiContext]);

    if (!supplierModel || !userModel || !vendorModel) {
        return null;
    }

    return (
        <div className={"AdminActionsCircle d-print-none " + (isVisible ? "" : "d-none")} style={style}>
            <div className="circle" onClick={() => setIsOpen(!isOpen)}>
                <div className="icon">
                    Admin <i className="fas fa-cog"></i>
                </div>
            </div>

            <div className="actions" style={actionsStyle}>
                {/* <div className="d-flex justify-content-end">
                <div>
                    <OwButton
                        fn={() => setIsVisible(false)}
                        centerIconClass="fas fa-times"
                        className="btn-danger"
                    />
                </div>
            </div> */}
                <div className="action">
                    <OwButton
                        fn={() => {
                            uiContext.setShowHiddenFeatures(!uiContext.showHiddenFeatures);
                        }}
                        title={"toggle_hidden_features"}
                    />
                </div>
                <div className="action mt-2">
                    <OwButton
                        fn={() => {
                            uiContext.setDisableIdleTimeout(!uiContext.disableIdleTimeout);
                        }}
                        title={uiContext.disableIdleTimeout ? "enable_idle_timeout" : "disable_idle_timeout"}
                    />
                    {!uiContext.disableIdleTimeout && (
                        <div className="mt-2 text-start">
                            <small className="text-muted">Idle Timer:</small>
                            <div className="fw-bold">
                                {(() => {
                                    const minutes = Math.floor(idleTimeDisplay / 60);
                                    const seconds = idleTimeDisplay % 60;
                                    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
                                })()}
                            </div>
                        </div>
                    )}
                </div>
                <div className="action mt-2">
                    <OwButton fn={switchSides} title={"switch_sides"} />
                </div>
                <div className="action mt-2">
                    <OwButton fn={showToast} title={"show_toast"} />
                </div>
                <div className="action mt-2">
                    <OwButton fn={closeTool} title={"close_tool"} />
                </div>
                <div className="action mt-2">
                    {!adminLogin ? (
                        <OwButton fn={() => setShowAdminLoginModal(true)} title={"admin_login"} />
                    ) : (
                        <div>
                            <OwLabel title="admin_logged_in" isBold={true} isUnderline={true} />
                            <div>
                                <ClickToCopy text={adminLogin.admin?.email || ""} noFlexGrow={true} />
                            </div>
                            <div className="mt-2">
                                <OwButton fn={handleAdminLogout} title={"admin_logout"} className="btn-danger btn-sm" />
                            </div>
                        </div>
                    )}
                </div>
                <div className="action mt-2">
                    <OwLabel title="supplier" isBold={true} isUnderline={true} />
                    <div>
                        <ClickToCopy text={supplierModel.name} noFlexGrow={true} />
                    </div>
                    <div>
                        <ClickToCopy label="-- GUID" text={supplierModel.guid} noFlexGrow={true} />
                    </div>
                    <div>
                        <ClickToCopy label="-- ID" text={supplierModel.id?.toString()} noFlexGrow={true} />
                    </div>
                </div>
                <div className="action mt-2">
                    <OwLabel title="account" isBold={true} isUnderline={true} />
                    <div>
                        <ClickToCopy text={vendorModel.name} noFlexGrow={true} />
                    </div>
                    <div>
                        <ClickToCopy label="-- GUID" text={vendorModel.guid} noFlexGrow={true} />
                    </div>
                    <div>
                        <ClickToCopy label="-- ID" text={vendorModel.id?.toString()} noFlexGrow={true} />
                    </div>
                </div>
                <div className="action mt-2">
                    <OwLabel title="user" isBold={true} isUnderline={true} />
                    <div>
                        <ClickToCopy text={userModel.name} noFlexGrow={true} />
                    </div>
                    <div>
                        <ClickToCopy label="-- Email" text={userModel.email} noFlexGrow={true} />
                    </div>
                    <div>
                        <ClickToCopy label="-- GUID" text={userModel.guid} noFlexGrow={true} />
                    </div>
                    <div>
                        <ClickToCopy label="-- ID" text={userModel.id?.toString()} noFlexGrow={true} />
                    </div>
                </div>
                <div className="mt-2">
                    <div>
                        <b>Resolution</b>
                    </div>
                    <div>
                        Viewport: {viewport.width}x{viewport.height}
                    </div>
                    <div>
                        Screen: {screen.width}x{screen.height}
                    </div>
                </div>
            </div>

            {showAdminLoginModal && (
                <AdminLoginModal
                    closeModal={() => setShowAdminLoginModal(false)}
                    onLoginSuccess={handleAdminLoginSuccess}
                    notifyFn={uiContext.notifyFn}
                />
            )}
        </div>
    );
};
